import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Logo from '@/components/logo';
import { DashboardPreview } from '@/components/landing/dashboard-preview';
import { MobileDashboardPreview } from '@/components/landing/mobile-dashboard-preview';
import { LandingFeatures } from '@/components/landing/landing-features';
import { KhodoAI } from '@/components/landing/khodo-ai';
import { ThemeProvider, useTheme } from '@/contexts/theme-context';
import { useAuth } from '@/hooks/use-auth';
import { Menu, X, ChevronRight, Moon, Sun, Sparkles, Layers, Scan, BarChart2, TriangleAlert, FileCheck } from 'lucide-react';

function LandingPageContent() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignIn = () => navigate('/login');
  const handleOpenAccount = () => navigate('/signup');

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''} bg-slate-50 text-slate-900 dark:bg-[#0A0A0B] dark:text-slate-50 selection:bg-blue-500/30 font-sans tracking-tight overflow-x-hidden transition-colors duration-500`}>

      {/* Khodo AI Chatbot */}
      <KhodoAI />

      {/* Dynamic Header */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled
        ? 'py-3 border-b border-slate-200/50 dark:border-white/5 bg-white/70 dark:bg-[#0A0A0B]/70 backdrop-blur-2xl shadow-sm'
        : 'py-6 bg-transparent'
        }`}>
        <div className="container mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <a href="/landing" className="flex items-center gap-2.5 group transition-transform hover:scale-105 duration-300">
              <Logo size="sm" showText={true} />
            </a>
            <div className="hidden md:flex items-center gap-8 text-sm font-medium">
              {[
                { name: 'How it Works', href: '#simplicity' },
                { name: 'Peace of Mind', href: '#peace-of-mind' },
                { name: 'Safety', href: '#safety' }
              ].map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={() => toggleTheme()}
              className="p-2.5 rounded-full text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-200/50 dark:hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              title="Toggle theme"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>

            <div className="hidden md:flex items-center gap-3 ml-2">
              <Button
                variant="ghost"
                className="text-sm font-semibold hover:bg-slate-200/50 dark:hover:bg-white/10 text-slate-700 dark:text-slate-300 px-6 transition-all"
                onClick={handleSignIn}
              >
                Log In
              </Button>
              <Button
                className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-white dark:text-black dark:hover:bg-slate-200 rounded-full px-7 py-2 text-sm font-bold shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5 active:translate-y-0"
                onClick={handleOpenAccount}
              >
                Get Started
              </Button>
            </div>

            <button
              className="md:hidden p-2 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`fixed inset-0 top-[72px] z-40 md:hidden bg-white/95 dark:bg-[#0A0A0B]/95 backdrop-blur-3xl transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-8'
          }`}>
          <div className="container mx-auto px-6 py-8 flex flex-col gap-6 h-full">
            <div className="flex flex-col gap-2">
              {[
                { name: 'How it Works', href: '#simplicity' },
                { name: 'Peace of Mind', href: '#peace-of-mind' },
                { name: 'Safety', href: '#safety' }
              ].map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-2xl font-bold text-slate-800 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors py-4 border-b border-slate-200/50 dark:border-white/5"
                >
                  {link.name}
                </a>
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-4">
              <Button
                variant="outline"
                className="w-full h-14 rounded-2xl text-lg font-bold border-slate-300 dark:border-white/10 dark:text-white hover:bg-slate-50 dark:hover:bg-white/5"
                onClick={handleSignIn}
              >
                Log In
              </Button>
              <Button
                className="w-full h-14 rounded-2xl text-lg font-bold bg-blue-600 text-white hover:bg-blue-700 dark:bg-white dark:text-black shadow-xl shadow-blue-500/20"
                onClick={handleOpenAccount}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-0 pt-32 lg:pt-48 pb-20 lg:pb-32 overflow-hidden min-h-screen flex items-center">
        {/* Background Elements - FIXED visibility */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=2800"
            alt="Kwacha AI Financial Background"
            className="w-full h-full object-cover object-center block opacity-100"
            loading="eager"
          />
          {/* Lightened overlays to let the image through */}
          <div className="absolute inset-0 bg-slate-50/20 dark:bg-[#0A0A0B]/40 z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/20 to-slate-50/80 dark:via-[#0A0A0B]/30 dark:to-[#0A0A0B]/90 z-20" />
        </div>

        {/* Ambient Glows */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-blue-500/10 dark:bg-blue-500/15 rounded-full blur-[120px] z-20 pointer-events-none" />

        <div className="container mx-auto px-6 relative z-30">
          <div className="flex flex-col items-center gap-16 lg:gap-24">

            {/* Hero Text */}
            <div className="w-full max-w-4xl space-y-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-600/20 bg-blue-600/5 text-blue-700 dark:text-blue-400 text-xs font-bold uppercase tracking-widest mx-auto shadow-sm backdrop-blur-md">
                <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 animate-pulse" />
                Meet Khodo: Your New Financial AI
              </div>

              <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-[90px] font-extrabold leading-[1.05] tracking-tight">
                Money, made <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-500 dark:from-blue-400 dark:via-indigo-400 dark:to-blue-300">
                  human.
                </span>
              </h1>

              <p className="text-lg md:text-2xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed font-normal">
                Stop stressing over spreadsheets. We translate your complex finances into <span className="text-slate-900 dark:text-white font-semibold">simple, actionable insights.</span>
              </p>

              <div className="flex justify-center pt-6">
                <Button
                  size="lg"
                  className="w-full sm:w-auto h-16 px-10 bg-blue-600 hover:bg-blue-700 dark:bg-white dark:text-black dark:hover:bg-slate-200 rounded-full text-lg font-bold group transition-all duration-300 shadow-2xl shadow-blue-500/20 hover:shadow-blue-500/30 hover:-translate-y-1"
                  onClick={handleOpenAccount}
                >
                  Start Your Journey
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1.5 transition-transform" />
                </Button>
              </div>
            </div>

            {/* Right Content - Dashboard Previews */}
            <div className="w-full relative flex justify-center">
              <div className="w-full max-w-6xl">
                {/* Desktop + Mobile Preview (Desktop Only) */}
                <div className="hidden lg:grid grid-cols-12 gap-8 items-end relative">

                  {/* Decorative Glows */}
                  <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-[100px] pointer-events-none" />
                  <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none" />

                  {/* Desktop Mockup */}
                  <div className="col-span-8 relative z-10 bg-[#0f1012] border border-slate-200/20 dark:border-white/10 rounded-2xl overflow-hidden shadow-[0_20px_100px_-20px_rgba(0,0,0,0.3)] dark:shadow-[0_0_100px_rgba(0,0,0,0.5)] transition-transform duration-700 hover:scale-[1.01]">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500/80" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                        <div className="w-3 h-3 rounded-full bg-green-500/80" />
                      </div>
                      <div className="ml-3 text-[11px] text-white/40 font-mono tracking-widest font-medium">APP.KWACHALITE.COM</div>
                    </div>
                    <div className="aspect-[16/10] bg-[#0A0A0B] overflow-hidden">
                      <DashboardPreview isDark={true} />
                    </div>
                  </div>

                  {/* Phone Mockup */}
                  <div className="col-span-4 relative z-20 flex justify-center -ml-20 mb-[-30px]">
                    <div className="relative p-2.5 bg-[#080809] border-[6px] border-[#2A2A2E] rounded-[3.5rem] shadow-[0_30px_100px_rgba(0,0,0,0.5)] dark:shadow-[0_50px_100px_rgba(0,0,0,0.8)] overflow-hidden w-[280px] transition-transform duration-700 hover:rotate-[-2deg] hover:scale-105">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-[#2A2A2E] rounded-b-3xl z-30 flex items-center justify-center">
                        <div className="w-12 h-1 bg-white/20 rounded-full mb-1" />
                      </div>
                      <div className="aspect-[9/19.5] rounded-[3rem] overflow-hidden bg-black ring-1 ring-white/10">
                        <MobileDashboardPreview isDark={true} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile Preview (Mobile Only) */}
                <div className="lg:hidden mt-8">
                  <div className="relative mx-auto" style={{ maxWidth: '300px' }}>
                    <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-3xl opacity-60" />
                    <div className="relative bg-[#080809] border-[6px] border-[#2A2A2E] rounded-[3rem] p-1.5 shadow-2xl">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#2A2A2E] rounded-b-2xl z-30" />
                      <div className="aspect-[9/19.5] rounded-[2.5rem] overflow-hidden ring-1 ring-white/10">
                        <MobileDashboardPreview isDark={true} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Khodo AI Intro Section */}
      <section className="py-24 bg-white dark:bg-[#0A0A0B] relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-700 to-indigo-900 rounded-[2.5rem] lg:rounded-[3.5rem] p-10 lg:p-20 overflow-hidden relative shadow-2xl border border-slate-100 dark:border-white/10 text-white">

            {/* Ambient Backgrounds */}
            <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
            <div className="absolute -right-32 -bottom-32 w-[500px] h-[500px] bg-blue-400/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-indigo-500/20 rounded-full blur-[100px] pointer-events-none" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12 items-center">

              {/* Text Content */}
              <div className="space-y-8 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold uppercase tracking-widest border border-white/20 backdrop-blur-md">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  Always Online
                </div>
                <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white tracking-tight leading-[1.1]">
                  Khodo AI: Your Personal <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">
                    Financial Companion.
                  </span>
                </h2>
                <p className="text-lg md:text-xl text-blue-100 font-light leading-relaxed max-w-xl mx-auto lg:mx-0 opacity-90">
                  Khodo isn't just a chatbot. It's a highly trained AI that understands your saving goals and helps you build a budget that actually works for your life.
                </p>

                <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-2">
                  <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/10 text-white hover:bg-white/15 transition-colors">
                    <p className="text-[10px] font-bold opacity-70 uppercase mb-1.5 tracking-widest">Savings Expert</p>
                    <p className="font-semibold text-sm">Optimize emergency funds</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/10 text-white hover:bg-white/15 transition-colors">
                    <p className="text-[10px] font-bold opacity-70 uppercase mb-1.5 tracking-widest">Budget Coach</p>
                    <p className="font-semibold text-sm">Real-time spending alerts</p>
                  </div>
                </div>
              </div>

              {/* Chat UI Mockup */}
              <div className="relative flex justify-center lg:justify-end perspective-1000">
                <div className="w-full max-w-[380px] bg-slate-50 dark:bg-[#12141A] rounded-[2rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] border border-white/20 dark:border-white/10 overflow-hidden transform lg:rotate-y-[-10deg] lg:rotate-z-[2deg] hover:rotate-0 transition-all duration-700">

                  {/* Chat Header */}
                  <div className="px-6 py-5 bg-white dark:bg-[#181B22] border-b border-slate-200 dark:border-white/5 flex items-center gap-4">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center text-white shadow-md">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-[#181B22] rounded-full"></div>
                    </div>
                    <div>
                      <div className="font-bold text-slate-900 dark:text-white text-sm">Khodo AI</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">Active now</div>
                    </div>
                  </div>

                  {/* Chat Body */}
                  <div className="p-6 space-y-5 bg-slate-50/50 dark:bg-transparent">
                    {/* AI Bubble */}
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex-shrink-0 flex items-center justify-center mt-1">
                        <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="bg-white dark:bg-[#1C1F26] border border-slate-100 dark:border-white/5 p-4 rounded-2xl rounded-tl-sm text-sm text-slate-700 dark:text-slate-200 shadow-sm leading-relaxed">
                        I noticed you could save <span className="font-semibold text-slate-900 dark:text-white">K 45,000</span> this month by reducing your dining out budget. Want me to set a limit?
                      </div>
                    </div>

                    {/* User Bubble */}
                    <div className="bg-blue-600 text-white p-4 rounded-2xl rounded-tr-sm text-sm self-end ml-12 text-right shadow-md leading-relaxed">
                      Yes, please! Let's do it.
                    </div>

                    {/* AI Bubble */}
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex-shrink-0 flex items-center justify-center mt-1">
                        <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="bg-white dark:bg-[#1C1F26] border border-slate-100 dark:border-white/5 p-4 rounded-2xl rounded-tl-sm text-sm text-slate-700 dark:text-slate-200 shadow-sm leading-relaxed">
                        Done! I'll notify you if you're getting close to that K 20,000 mark. 🚀
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Kwacha AI Intelligence Section */}
      <section className="py-24 relative overflow-hidden bg-slate-50 dark:bg-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-600/10 text-blue-700 dark:text-blue-400 text-xs font-bold uppercase tracking-widest border border-blue-600/20 mb-6">
              Powered by Kwacha AI
            </div>
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
              Financial intelligence, <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">built-in.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Beyond simple tracking. We've built a suite of AI-powered tools that think, learn, and grow with your finances.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* ML Categorization */}
            <div className="group p-8 rounded-[2rem] bg-white dark:bg-[#12141A] border border-slate-200 dark:border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6 group-hover:scale-110 transition-transform">
                <Layers className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold mb-3">Smart Categorization</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                Our ML model learns from Malawian merchants like Airtel and Shoprite to categorize your spending with 95%+ accuracy.
              </p>
            </div>

            {/* OCR Scanning */}
            <div className="group p-8 rounded-[2rem] bg-white dark:bg-[#12141A] border border-slate-200 dark:border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-6 group-hover:scale-110 transition-transform">
                <Scan className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold mb-3">Snapshot Scanning</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                Forget manual entry. Take a photo of any receipt and let Kwacha AI extract the merchant, amount, and date in seconds.
              </p>
            </div>

            {/* Financial Forecasting */}
            <div className="group p-8 rounded-[2rem] bg-white dark:bg-[#12141A] border border-slate-200 dark:border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-6 group-hover:scale-110 transition-transform">
                <BarChart2 className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold mb-3">Khodo AI Forecasts</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                See into the future. Our integration with Facebook Prophet predicts your spending for the next 30 days based on your history.
              </p>
            </div>

            {/* Anomaly Detection */}
            <div className="group p-8 rounded-[2rem] bg-white dark:bg-[#12141A] border border-slate-200 dark:border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-rose-600 dark:text-rose-400 mb-6 group-hover:scale-110 transition-transform">
                <TriangleAlert className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold mb-3">Anomaly Detection</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                Isolation Forest AI flags unusual transactions and potential billing errors automatically to protect your budget.
              </p>
            </div>

            {/* Professional PDFs */}
            <div className="group p-8 rounded-[2rem] bg-white dark:bg-[#12141A] border border-slate-200 dark:border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 lg:col-span-2">
              <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 flex-shrink-0">
                  <FileCheck className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-3">Audit-Ready Documentation</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed max-w-xl">
                    Generate professional, branded invoices and quotes using our server-side PDF engine. High-fidelity documents, instantly ready for your clients or tax audits.
                  </p>
                </div>
                <div className="hidden md:block ml-auto bg-blue-600 text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest">
                  New Feature
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modular Landing Features */}
      <LandingFeatures />

    </div>
  );
}

export default function LandingPage() {
  return (
    <ThemeProvider>
      <LandingPageContent />
    </ThemeProvider>
  );
}